import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Sparkles,
  FileText,
  Building2,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Target,
  Rocket,
  Star,
  ArrowUpRight,
  ChevronDown,
  Code2,
  Network,
  RefreshCw,
  ShieldCheck,
  Cloud,
  UserCog,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Section, Container } from '@/components/ui/Section';
import { LocalizedLink } from '@/components/LocalizedLink';
import { AnimatedBackground, AnimatedCounter } from '@/components/Hero';

export function Home() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const services = [
    {
      icon: Code2,
      title: t('home.services.dev.title'),
      tagline: t('home.services.dev.tagline'),
      description: t('home.services.dev.description'),
      features: t('home.services.dev.features', { returnObjects: true }) as string[],
      route: 'service_dev',
    },
    {
      icon: Network,
      title: t('home.services.integration.title'),
      tagline: t('home.services.integration.tagline'),
      description: t('home.services.integration.description'),
      features: t('home.services.integration.features', { returnObjects: true }) as string[],
      route: 'service_integration',
    },
    {
      icon: RefreshCw,
      title: t('home.services.transformation.title'),
      tagline: t('home.services.transformation.tagline'),
      description: t('home.services.transformation.description'),
      features: t('home.services.transformation.features', { returnObjects: true }) as string[],
      route: 'service_transformation',
    },
    {
      icon: ShieldCheck,
      title: t('home.services.security.title'),
      tagline: t('home.services.security.tagline'),
      description: t('home.services.security.description'),
      features: t('home.services.security.features', { returnObjects: true }) as string[],
      route: 'service_security',
    },
    {
      icon: Cloud,
      title: t('home.services.cloud.title'),
      tagline: t('home.services.cloud.tagline'),
      description: t('home.services.cloud.description'),
      features: t('home.services.cloud.features', { returnObjects: true }) as string[],
      route: 'service_cloud',
    },
    {
      icon: UserCog,
      title: t('home.services.consulting.title'),
      tagline: t('home.services.consulting.tagline'),
      description: t('home.services.consulting.description'),
      features: t('home.services.consulting.features', { returnObjects: true }) as string[],
      route: 'service_consulting',
    },
  ];

  const products = [
    {
      icon: FileText,
      title: 'FirmoSEMPLICE',
      badge: t('home.products.firmo.badgeShort'),
      description: t('home.products.firmo.description'),
      painPoint: t('home.products.firmo.painPoint'),
      features: t('home.products.firmo.features', { returnObjects: true }) as string[],
      trustBadge: t('home.products.firmo.trustBadge'),
      route: 'product_firmo',
      gradient: 'from-cta-400 to-cta-600',
      bgGradient: 'from-cta-500 to-cta-600',
    },
    {
      icon: Sparkles,
      title: 'SempliceAI',
      badge: t('home.products.ai.badgeShort'),
      description: t('home.products.ai.description'),
      painPoint: t('home.products.ai.painPoint'),
      features: t('home.products.ai.features', { returnObjects: true }) as string[],
      trustBadge: t('home.products.ai.trustBadge'),
      route: 'product_ai',
      gradient: 'from-accent-400 to-accent-600',
      bgGradient: 'from-accent-500 to-accent-600',
    },
    {
      icon: Building2,
      title: 'SemplicePA',
      badge: t('home.products.pa.badgeShort'),
      description: t('home.products.pa.description'),
      painPoint: t('home.products.pa.painPoint'),
      features: t('home.products.pa.features', { returnObjects: true }) as string[],
      trustBadge: t('home.products.pa.trustBadge'),
      route: 'product_pa',
      gradient: 'from-accent-400 to-accent-600',
      bgGradient: 'from-accent-500 to-accent-600',
    },
  ];

  const businessUnits = [
    {
      id: 'sistemi',
      name: t('home.businessUnits.sistemi.name'),
      tagline: t('home.businessUnits.sistemi.tagline'),
      description: t('home.businessUnits.sistemi.descriptionShort'),
      icon: Target,
      gradient: 'from-info-400 to-info-600',
      stats: { value: '500+', label: t('home.businessUnits.sistemi.statsLabel') },
    },
    {
      id: 'semplice',
      name: t('home.businessUnits.semplice.name'),
      tagline: t('home.businessUnits.semplice.tagline'),
      description: t('home.businessUnits.semplice.descriptionShort'),
      icon: Lightbulb,
      gradient: 'from-accent-400 to-accent-600',
      stats: { value: 'AI', label: t('home.businessUnits.semplice.statsLabel') },
    },
    {
      id: 'firma',
      name: t('home.businessUnits.firma.name'),
      tagline: t('home.businessUnits.firma.tagline'),
      description: t('home.businessUnits.firma.descriptionShort'),
      icon: Rocket,
      gradient: 'from-cta-400 to-cta-600',
      stats: { value: 'CSA', label: t('home.businessUnits.firma.statsLabel') },
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('home.pageTitle')}</title>
        <meta name="description" content={t('home.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/`} />
      </Helmet>

      {/* Hero Section - KEEP AS IS */}
      <section className="relative min-h-[calc(100vh-5rem)] flex items-center overflow-hidden">
        <AnimatedBackground />

        <Container className="relative z-10 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-6 lg:space-y-8">
              {/* Animated Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50/80 backdrop-blur-sm rounded-full text-sm font-medium text-primary-600 border border-primary-100 animate-pulse-glow">
                  <Sparkles className="w-4 h-4" />
                  {t('home.badge')}
                </span>
              </motion.div>

              {/* Title with Gradient */}
              <motion.h1
                className="heading-1 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {t('home.heroTitle')}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className="text-lg sm:text-xl text-gray-600 max-w-xl leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {t('home.heroDescription')}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-wrap items-center gap-4 pt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <LocalizedLink to="contacts">
                  <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <button className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 text-base bg-primary-400 text-white shadow-glow h-12 cursor-pointer">
                      {t('buttons.contactUs')}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                  </motion.div>
                </LocalizedLink>
                <LocalizedLink to="about">
                  <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <button className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 text-base border-2 border-primary-400 text-primary-400 h-12 cursor-pointer">
                      {t('buttons.aboutUs')}
                    </button>
                  </motion.div>
                </LocalizedLink>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                className="flex items-center gap-6 pt-4 text-sm text-gray-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cta-500" />
                  <span>{t('home.freeConsulting')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cta-500" />
                  <span>{t('home.support247')}</span>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Business Units Grid */}
            <div className="relative hidden md:block">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/60 shadow-2xl shadow-pink-200/50"
              >
                {/* Grid Header */}
                <div className="text-center mb-6">
                  <span className="text-sm font-semibold text-info-500 uppercase tracking-wider">
                    {t('home.heroGrid.title')}
                  </span>
                </div>

                {/* 3-Column Grid */}
                <div className="grid grid-cols-3 gap-4">
                  {/* 01Sistemi */}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                    className="group relative bg-gradient-to-br from-info-500 to-info-600 rounded-2xl p-5 flex flex-col items-center justify-center min-h-[120px] shadow-lg shadow-info-200 cursor-pointer overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="text-white font-bold text-lg text-center">01Sistemi</span>
                    <span className="text-white/70 text-xs mt-1">System Integration</span>
                  </motion.div>

                  {/* 01Semplice */}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                    className="group relative bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl p-5 flex flex-col items-center justify-center min-h-[120px] shadow-lg shadow-accent-200 cursor-pointer overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="text-white font-bold text-lg text-center">01Semplice</span>
                    <span className="text-white/70 text-xs mt-1">AI & Innovation</span>
                  </motion.div>

                  {/* FirmaSPID */}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                    className="group relative bg-gradient-to-br from-cta-500 to-cta-600 rounded-2xl p-5 flex flex-col items-center justify-center min-h-[120px] shadow-lg shadow-cta-200 cursor-pointer overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="text-white font-bold text-lg text-center">FirmaSPID</span>
                    <span className="text-white/70 text-xs mt-1">Digital Identity</span>
                  </motion.div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200/50">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-info-500">
                      <AnimatedCounter target={20} suffix="+" />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('home.yearsExperience')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-accent-500">
                      <AnimatedCounter target={500} suffix="+" />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('home.projectsDelivered')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cta-500">
                      <AnimatedCounter target={99} suffix="%" />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('home.satisfiedCustomers')}</div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-28 h-28 bg-gradient-to-br from-primary-400/20 to-accent-400/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-36 h-36 bg-gradient-to-br from-cta-400/20 to-info-400/20 rounded-full blur-2xl" />
            </div>
          </div>

          {/* Scroll Down Arrow - Hidden on mobile */}
          <motion.a
            href="#business-units"
            className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 group cursor-pointer"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            aria-label={t('aria.scrollDown')}
          >
            <span className="text-xs font-medium text-gray-500 group-hover:text-primary-500 transition-colors">
              {t('home.labels.scrollDown')}
            </span>
            <motion.div
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm border border-pink-200 flex items-center justify-center shadow-lg group-hover:bg-primary-400 group-hover:border-primary-400 transition-all duration-300"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-5 h-5 text-primary-500 group-hover:text-white transition-colors" />
            </motion.div>
          </motion.a>
        </Container>
      </section>

      {/* Business Units - Interactive Cards */}
      <Section id="business-units" className="relative overflow-hidden scroll-mt-24" padding="lg">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/60 via-rose-50/80 to-pink-50/60" />
        <div className="absolute top-0 left-0 w-full h-full bg-white/40 backdrop-blur-sm" />
        <div className="absolute top-20 right-[10%] w-96 h-96 bg-pink-300/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 left-[5%] w-72 h-72 bg-rose-200/40 rounded-full blur-[100px]" />

        <Container className="relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full text-sm font-medium text-primary-600 border border-pink-200 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Building2 className="w-4 h-4" />
              {t('home.buSection.overline')}
            </motion.span>
            <motion.h2
              className="heading-2 mt-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('home.buSection.titleAlt')}
            </motion.h2>
            <motion.p
              className="body-regular mt-3 text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t('home.buSection.subtitle')}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {businessUnits.map((bu, index) => (
              <motion.div
                key={bu.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <LocalizedLink to={bu.id === 'sistemi' ? 'about' : bu.id === 'semplice' ? 'product_ai' : 'product_firmo'}>
                  <Card
                    variant="hover"
                    className="h-full relative overflow-hidden group bg-white/80 backdrop-blur-sm border border-pink-100/50 hover:border-pink-300 hover:shadow-2xl hover:shadow-pink-200/50 transition-all duration-500 cursor-pointer"
                  >
                    {/* Gradient top border */}
                    <div
                      className={cn(
                        'absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r',
                        bu.gradient
                      )}
                    />

                    {/* Background glow on hover */}
                    <div
                      className={cn(
                        'absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[60px] opacity-0 group-hover:opacity-30 transition-opacity duration-500 bg-gradient-to-br',
                        bu.gradient
                      )}
                    />

                    <div className="relative z-10">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div
                          className={cn(
                            'w-14 h-14 rounded-2xl bg-gradient-to-br flex items-center justify-center shadow-lg',
                            bu.gradient
                          )}
                        >
                          <bu.icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                            {bu.stats.value}
                          </div>
                          <div className="text-xs text-gray-500">{bu.stats.label}</div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="mb-4">
                        <span
                          className={cn(
                            'text-xs font-semibold uppercase tracking-wider bg-gradient-to-r bg-clip-text text-transparent',
                            bu.gradient
                          )}
                        >
                          {bu.tagline}
                        </span>
                        <h3 className="heading-3 mt-1 group-hover:text-primary-500 transition-colors">
                          {bu.name}
                        </h3>
                      </div>

                      <p className="body-small text-gray-600 mb-6">{bu.description}</p>

                      {/* CTA */}
                      <div className="flex items-center text-sm font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                        <span>{t('home.labels.learnMore')}</span>
                        <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </div>
                    </div>
                  </Card>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Products - Comparison Cards Layout */}
      <Section padding="lg" className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50/50 to-white" />

        <Container className="relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <motion.span
              className="caption text-primary-400"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              {t('home.productsSection.overline')}
            </motion.span>
            <motion.h2
              className="heading-2 mt-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('home.productsSection.title')}
            </motion.h2>
            <motion.p
              className="body-regular mt-3 text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t('home.productsSection.subtitle')}
            </motion.p>
          </div>

          {/* Comparison Cards Grid - 3 Equal Columns */}
          <div className="grid md:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + index * 0.1 }}
              >
                <LocalizedLink to={product.route as any}>
                  <Card
                    variant="hover"
                    className="h-full relative overflow-hidden group bg-white border border-gray-100 hover:border-gray-200 transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/50"
                  >
                    {/* Colored Header */}
                    <div
                      className={cn(
                        'relative h-32 bg-gradient-to-br flex flex-col items-center justify-center p-6',
                        product.bgGradient
                      )}
                    >
                      {/* Pattern overlay */}
                      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_2px_2px,white_1px,transparent_0)] bg-[length:20px_20px]" />

                      {/* Icon */}
                      <div className="relative z-10 w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2">
                        <product.icon className="w-7 h-7 text-white" />
                      </div>

                      {/* Badge */}
                      <span className="relative z-10 text-xs font-medium text-white/90 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        {product.badge}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col" style={{ minHeight: 'calc(100% - 8rem)' }}>
                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                        {product.title}
                      </h3>

                      {/* Pain Point */}
                      <p className="text-sm text-primary-500 font-medium text-center mb-4">
                        {product.painPoint}
                      </p>

                      {/* Features List */}
                      <ul className="space-y-3 mb-6 flex-grow">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className={cn(
                              'w-5 h-5 flex-shrink-0 mt-0.5',
                              index === 0 ? 'text-cta-500' : 'text-accent-500'
                            )} />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Trust Badge */}
                      <div className="flex justify-center mb-4">
                        <span className={cn(
                          'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full',
                          index === 0 ? 'bg-cta-50 text-cta-600' : 'bg-accent-50 text-accent-600'
                        )}>
                          <Star className="w-3.5 h-3.5" />
                          {product.trustBadge}
                        </span>
                      </div>

                      {/* CTA Button */}
                      <div
                        className={cn(
                          'w-full py-3 px-4 rounded-lg text-sm font-semibold text-center text-white bg-gradient-to-r transition-all duration-300 group-hover:shadow-lg',
                          product.gradient
                        )}
                      >
                        {t('home.productsSection.discoverProduct')}
                      </div>
                    </div>
                  </Card>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Services - Feature Cards */}
      <Section className="relative overflow-hidden" padding="lg">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-primary-100/40 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-accent-100/30 to-transparent rounded-full blur-3xl" />

        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium text-primary-600 border border-primary-100 shadow-sm mb-6"
            >
              <Zap className="w-4 h-4" />
              {t('home.servicesSection.overline')}
            </motion.div>
            <motion.h2
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('home.servicesSection.title')}
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t('home.servicesSection.description')}
            </motion.p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + index * 0.1 }}
              >
                <LocalizedLink to={service.route as any}>
                  <Card
                    variant="hover"
                    className="h-full flex flex-col relative overflow-hidden group bg-white border-0 shadow-lg shadow-gray-200/50 hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-500"
                  >
                    {/* Top accent line */}
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-400 to-primary-600" />

                    <div className="p-8 flex flex-col flex-grow">
                      {/* Icon and Arrow */}
                      <div className="flex items-start justify-between mb-6">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-primary-50 text-primary-600">
                          <service.icon className="w-8 h-8" />
                        </div>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-gray-100 group-hover:bg-gradient-to-r group-hover:from-primary-400 group-hover:to-primary-600 group-hover:text-white">
                          <ArrowUpRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm font-semibold mb-4 text-primary-600">
                        {service.tagline}
                      </p>
                      <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                        {service.description}
                      </p>

                      {/* Bottom CTA */}
                      <div>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 transition-all duration-300">
                          {t('buttons.learnMore')}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Card>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>

        </Container>
      </Section>
    </>
  );
}
